/**
 * Global Documentation Search Page
 * Searches across articles, passwords, configurations, flexible assets, and expirations
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, FileText, Key, Server, Box, Calendar, Clock,
  Filter, X, Loader2, ChevronRight, Lock, Globe, Building2,
  AlertCircle
} from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { useDebounce } from '../../hooks/useDebounce';
import { isInternalRole } from '../../utils/roles.js';

// Type configuration with icons and labels
const TYPE_CONFIG = {
  article: {
    icon: FileText,
    label: 'Articles',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    path: (id) => `/documentation/articles/${id}`,
  },
  password: {
    icon: Key,
    label: 'Passwords',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    path: (id) => `/vault/passwords/${id}`,
  },
  configuration: {
    icon: Server,
    label: 'Configurations',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    path: (id) => `/documentation/configurations/${id}`,
  },
  flexible_asset: {
    icon: Box,
    label: 'Assets',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    path: (id) => `/documentation/flexible-assets/${id}`,
  },
  expiration: {
    icon: Calendar,
    label: 'Expirations',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    // There is no per-expiration page; the list is the destination.
    path: () => '/documentation/expirations',
  },
};

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Type filter chip
function TypeChip({ type, active, onClick }) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        active
          ? `${config.bgColor} ${config.color}`
          : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-600'
      }`}
    >
      <Icon className="w-4 h-4" />
      {config.label}
    </button>
  );
}

// Search result card
function SearchResultCard({ result, isInternal }) {
  const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.article;
  const Icon = config.icon;

  return (
    <Link
      to={config.path(result.id)}
      className="group flex items-start gap-4 p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-md transition-all"
    >
      <div className={`p-2 rounded-lg ${config.bgColor}`}>
        <Icon className={`w-5 h-5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-neutral-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 truncate">
            {result.name || result.title}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isInternal && result.visibility && (
              <VisibilityBadge visibility={result.visibility} />
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full ${config.bgColor} ${config.color}`}>
              {config.label.slice(0, -1)}
            </span>
          </div>
        </div>
        {result.description && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">
            {result.description}
          </p>
        )}
        <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          {result.folder && (
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {result.folder}
            </span>
          )}
          {result.location && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {result.location}
            </span>
          )}
          {result.assetType && (
            <span className="flex items-center gap-1">
              <Box className="w-3 h-3" />
              {result.assetType}
            </span>
          )}
          {result.updated_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(result.updated_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-brand-500 flex-shrink-0 self-center" />
    </Link>
  );
}

export default function GlobalSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeTypes, setActiveTypes] = useState(() => {
    const types = searchParams.get('types');
    return types ? types.split(',') : [];
  });
  const { user } = useAuthStore();

  const isInternal = isInternalRole(user?.role);
  const debouncedQuery = useDebounce(query, 300);

  // Update URL when search params change
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (activeTypes.length > 0) params.set('types', activeTypes.join(','));
    setSearchParams(params, { replace: true });
  }, [query, activeTypes, setSearchParams]);

  // Search query
  const { data: searchData, isLoading, error } = useQuery({
    queryKey: ['documentation', 'global-search', debouncedQuery, activeTypes],
    queryFn: () => documentationAPI.globalSearch({
      q: debouncedQuery,
      types: activeTypes.length > 0 ? activeTypes.join(',') : undefined,
      limit: 20,
    }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30000,
  });

  const results = searchData?.data?.results || [];
  const total = searchData?.data?.total || 0;

  const toggleType = useCallback((type) => {
    setActiveTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setActiveTypes([]);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Search is already reactive through debounce
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Search Documentation</h1>
        <p className="text-neutral-600 dark:text-neutral-400">
          Search across articles, passwords, configurations, assets, and expirations
        </p>
      </div>

      {/* Search input */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all documentation..."
            className="w-full pl-12 pr-4 py-3 text-lg border border-neutral-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm"
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full"
            >
              <X className="w-4 h-4 text-neutral-400" />
            </button>
          )}
        </div>
        {query.length > 0 && query.length < 2 && (
          <p className="mt-2 text-sm text-neutral-500">Enter at least 2 characters to search</p>
        )}
      </form>

      {/* Type filters */}
      <div className="mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-400 mr-2">
            <Filter className="w-4 h-4" />
            Filter by type:
          </span>
          {Object.keys(TYPE_CONFIG).map(type => (
            <TypeChip
              key={type}
              type={type}
              active={activeTypes.includes(type)}
              onClick={() => toggleType(type)}
            />
          ))}
          {activeTypes.length > 0 && (
            <button
              onClick={clearFilters}
              className="text-sm text-brand-600 dark:text-brand-400 hover:underline ml-2"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-red-200 dark:border-red-800">
            <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              Search Error
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              {error.response?.data?.error || 'An error occurred while searching'}
            </p>
          </div>
        ) : debouncedQuery.length >= 2 ? (
          results.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Found <span className="font-medium text-neutral-900 dark:text-white">{total}</span> result{total !== 1 ? 's' : ''} for "<span className="font-medium text-neutral-900 dark:text-white">{debouncedQuery}</span>"
                </p>
              </div>
              <div className="space-y-3">
                {results.map(result => (
                  <SearchResultCard
                    key={`${result.type}-${result.id}`}
                    result={result}
                    isInternal={isInternal}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <Search className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                No results found
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Try adjusting your search terms or filters
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <Search className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              Start searching
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
              Search across all your documentation including articles, passwords, configurations, flexible assets, and expirations.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
